import { keepPreviousData, QueryKey, useMutation, UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, BACKGROUND_POLL, clean, errorMessage, get, getPaged, Paged } from './client';
import { toast } from '../store/ui';

/**
 * GET one resource. `params` are part of the cache key. With `keepPrevious`, changing only the params keeps the last
 * answer for the same URL on screen (flagged by `isPlaceholderData`) while the new one loads.
 */
export function useGet<T>(url: string | null, params?: Record<string, unknown>, opts: { enabled?: boolean; refetchInterval?: number; keepPrevious?: boolean } = {}) {
  return useQuery<T, ApiError>({
    queryKey: [url, params ?? null],
    // a timer-driven refresh is not the person doing anything, so it must not keep an idle session alive
    queryFn: () => (opts.refetchInterval ? get<T>(url!, params, BACKGROUND_POLL) : get<T>(url!, params)),
    enabled: !!url && opts.enabled !== false,
    refetchInterval: opts.refetchInterval,
    placeholderData: opts.keepPrevious ? (prev, prevQuery) => (prevQuery?.queryKey[0] === url ? prev : undefined) : undefined,
  });
}

/** GET a paginated list ({ data, meta }). Keeps the previous page on screen while the next loads. */
export function useList<T>(url: string, params: Record<string, unknown>, opts: { enabled?: boolean } = {}) {
  return useQuery<Paged<T>, ApiError>({
    // empty filters are not sent, so they must not make a separate cache entry for what is the same request
    queryKey: [url, clean(params)],
    queryFn: () => getPaged<T>(url, params),
    placeholderData: keepPreviousData,
    enabled: opts.enabled !== false,
  });
}

/** The most records `useAllPages` brings in; the API allows 100 per request. */
export const ALL_PAGES_CAP = 1000;

/**
 * Every record a list matches (up to ALL_PAGES_CAP), fetched 100 at a time, for totals and counts that must cover the
 * whole list rather than one page of it. The first page shares its cache entry with `useList(url, { ...params, page: 1, limit: 100 })`.
 * `meta.total` is the real total, so a caller can tell when the cap cut the list short.
 */
export function useAllPages<T>(url: string, params: Record<string, unknown>, opts: { enabled?: boolean } = {}) {
  const qc = useQueryClient();
  return useQuery<Paged<T>, ApiError>({
    queryKey: [url, { ...params, all: ALL_PAGES_CAP }],
    queryFn: async () => {
      const first = await qc.fetchQuery({ queryKey: [url, clean({ ...params, page: 1, limit: 100 })], queryFn: () => getPaged<T>(url, { ...params, page: 1, limit: 100 }), staleTime: 30_000 });
      const total = first.meta.total;
      const pages = Math.ceil(Math.min(total, ALL_PAGES_CAP) / 100);
      const rest = await Promise.all(Array.from({ length: Math.max(0, pages - 1) }, (_, i) => getPaged<T>(url, { ...params, page: i + 2, limit: 100 })));
      return { data: [first, ...rest].flatMap((p) => p.data), meta: { ...first.meta, limit: ALL_PAGES_CAP } };
    },
    placeholderData: keepPreviousData,
    enabled: opts.enabled !== false,
  });
}

/**
 * Mutation that shows the server's message as a toast on failure and refreshes the listed query keys on
 * success. `invalidate` entries match by URL prefix, e.g. '/purchase-requests'.
 */
export function useAction<TData = unknown, TVars = void>(
  fn: (vars: TVars) => Promise<TData>,
  opts: { success?: string | ((d: TData) => string); invalidate?: string[]; onSuccess?: (d: TData, v: TVars) => void; silentError?: boolean } = {},
  extra: Omit<UseMutationOptions<TData, ApiError, TVars>, 'mutationFn'> = {},
) {
  const qc = useQueryClient();
  return useMutation<TData, ApiError, TVars>({
    mutationFn: fn,
    onSuccess: (d, v) => {
      for (const prefix of opts.invalidate ?? []) {
        void qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith(prefix) });
      }
      if (opts.success) toast(typeof opts.success === 'function' ? opts.success(d) : opts.success, 'good');
      opts.onSuccess?.(d, v);
    },
    onError: (e) => {
      if (!opts.silentError) toast(errorMessage(e), 'bad');
    },
    ...extra,
  });
}

export const invalidate = (qc: ReturnType<typeof useQueryClient>, ...prefixes: string[]) => {
  for (const prefix of prefixes) {
    void qc.invalidateQueries({ predicate: (q: { queryKey: QueryKey }) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith(prefix) });
  }
};
